import React, { useState } from 'react';
import './Contact.css';
import { submitToGoogleSheets, validateFormData } from '../utils/googleSheets';

const EMPTY = { fullName:'', mobile:'', email:'', country:'', service:'', message:'' };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const change = e => setForm({ ...form, [e.target.name]: e.target.value });
  
  const submit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate form data
    const validation = validateFormData(form);
    if (!validation.isValid) {
      setError(validation.errors[0]);
      return;
    }
    
    setBusy(true);
    
    try {
      // Submit to Google Sheets
      await submitToGoogleSheets(form, 'Contact Form');
      
      // Show success message
      setBusy(false);
      setDone(true);
      setForm(EMPTY);
      
      // Hide success message after 5 seconds
      setTimeout(() => setDone(false), 5000);
      
    } catch (err) {
      setBusy(false);
      setError(err.message || 'Failed to submit form. Please try again.');
    }
  };

  return (
    <section id="contact" className="sec">
      <div className="container">

        <div className="ct-grid">

          {/* ── LEFT: Form ── */}
          <div className="ct-left">
            <p className="label"><i className="fas fa-paper-plane"></i> Contact Us</p>
            <h2 className="ct-heading">Get in <span>Touch</span></h2>
            <p className="ct-sub">Have questions about your visa or travel documentation? We're here to help.</p>

            <form className="ct-form" onSubmit={submit}>
              <div className="ct-row">
                <div className="ct-field">
                  <label>Full Name *</label>
                  <input name="fullName" type="text" value={form.fullName}
                    onChange={change} placeholder="Your full name" required />
                </div>
                <div className="ct-field">
                  <label>Mobile Number *</label>
                  <input name="mobile" type="tel" value={form.mobile}
                    onChange={change} placeholder="+91 XXXXX XXXXX" required />
                </div>
              </div>

              <div className="ct-field">
                <label>Email Address *</label>
                <input name="email" type="email" value={form.email}
                  onChange={change} placeholder="your@email.com" required />
              </div>

              <div className="ct-row">
                <div className="ct-field">
                  <label>Destination Country *</label>
                  <select name="country" value={form.country} onChange={change} required>
                    <option value="">Select country</option>
                    {['Canada','Australia','United States','United Kingdom','Schengen (Europe)',
                      'Singapore','Malaysia','Thailand','Indonesia','UAE','New Zealand','Japan','Vietnam','Other'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div className="ct-field">
                  <label>Service Required *</label>
                  <select name="service" value={form.service} onChange={change} required>
                    <option value="">Select service</option>
                    {['Tourist Visa','eVisa Assistance','ETA Processing',
                      'Digital Arrival Card','Visa Consultation','Document Review',
                      'Travel Insurance','Other'].map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="ct-field">
                <label>Message (Optional)</label>
                <textarea name="message" value={form.message}
                  onChange={change} placeholder="Any specific requirements or questions..." rows={3}></textarea>
              </div>

              <button type="submit" className="ct-submit" disabled={busy}>
                {busy
                  ? <><i className="fas fa-spinner fa-spin"></i> Submitting...</>
                  : <><i className="fas fa-paper-plane"></i> Submit Inquiry</>
                }
              </button>

              {done && (
                <div className="ct-ok">
                  <i className="fas fa-circle-check"></i>
                  Thank you! We'll respond within 2–4 hours.
                </div>
              )}
              
              {error && (
                <div className="ct-error">
                  <i className="fas fa-circle-exclamation"></i>
                  {error}
                </div>
              )}
            </form>
          </div>

          {/* ── RIGHT: Contact info ── */}
          <div className="ct-right">

            {/* Address */}
            <div className="ct-brand">
              <div className="ct-address">
                <i className="fas fa-location-dot"></i>
                <span>Madhuban Tower, Road No. 4, Street No. 10,<br />Mahipalpur, New Delhi – 110037</span>
              </div>
            </div>

            {/* Contact cards */}
            <div className="ct-cards">
              <a href="tel:+918287908005" className="ct-card">
                <div className="ct-card-icon"><i className="fas fa-phone"></i></div>
                <div className="ct-card-body">
                  <span>Call Us</span>
                  <strong>+91 8287908005</strong>
                </div>
                <i className="fas fa-arrow-right ct-card-arr"></i>
              </a>

              <a href="https://wa.me/918287450060" target="_blank" rel="noopener noreferrer" className="ct-card">
                <div className="ct-card-icon wa"><i className="fab fa-whatsapp"></i></div>
                <div className="ct-card-body">
                  <span>WhatsApp</span>
                  <strong>+91 8287450060</strong>
                </div>
                <i className="fas fa-arrow-right ct-card-arr"></i>
              </a>

              <a href="mailto:hello@roamlife.in" className="ct-card">
                <div className="ct-card-icon teal"><i className="fas fa-envelope"></i></div>
                <div className="ct-card-body">
                  <span>Email</span>
                  <strong>hello@roamlife.in</strong>
                </div>
                <i className="fas fa-arrow-right ct-card-arr"></i>
              </a>
            </div>

            {/* Compact map */}
            <div className="ct-map">
              <iframe
                title="RoamLife Office"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3506.2!2d77.12!3d28.54!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2sMahipalpur+New+Delhi!5e0!3m2!1sen!2sin"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
              <a
                href="https://maps.google.com/?q=Madhuban+Tower+Mahipalpur+New+Delhi"
                target="_blank"
                rel="noopener noreferrer"
                className="ct-directions"
              >
                <i className="fas fa-directions"></i> Get Directions
              </a>
            </div>

            {/* Social + hours row */}
            <div className="ct-bottom-row">
              <div className="ct-social">
                <a href="https://www.facebook.com/DREAMAHOLIDAY" target="_blank" rel="noopener noreferrer" className="csoc fb" aria-label="Facebook"><i className="fab fa-facebook-f"></i></a>
                <a href="https://www.instagram.com/dreamaholiday/" target="_blank" rel="noopener noreferrer" className="csoc ig" aria-label="Instagram"><i className="fab fa-instagram"></i></a>
                <a href="https://www.linkedin.com/company/dreamaholidayindia/" target="_blank" rel="noopener noreferrer" className="csoc li" aria-label="LinkedIn"><i className="fab fa-linkedin-in"></i></a>
                <a href="https://wa.me/918287450060" target="_blank" rel="noopener noreferrer" className="csoc wa" aria-label="WhatsApp"><i className="fab fa-whatsapp"></i></a>
              </div>
              <div className="ct-hours">
                <i className="fas fa-clock"></i>
                <span>Mon–Sat 9am–7pm &nbsp;·&nbsp; Reply within 2–4 hrs</span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
}
